import {translate} from '@docusaurus/Translate';
import type {ReactNode} from 'react';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useLocation} from '@docusaurus/router';

import {MAX_QUESTION_LENGTH} from './assistantConversation';
import {canReferenceElement, getElementReference, getSelectionReference} from './assistantReference';
import styles from './styles.module.css';

type Reference = {content: string; left: number; top: number; keyboard: boolean; selection: boolean};

export default function PageReferenceMenu({onReference}: {onReference: (content: string) => boolean}): ReactNode {
  const location = useLocation();
  const [reference, setReference] = useState<Reference | null>(null);
  const [error, setError] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setReference(null);
    setError('');
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    let frameId = 0;
    let holdTimer = 0;
    let touchOrigin: {x: number; y: number} | null = null;
    let heldElement: Element | null = null;

    const showReference = (content: string, x: number, y: number, keyboard = false, selection = false) => {
      returnFocusRef.current = keyboard && document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setError('');
      setReference(content ? {
        content,
        left: Math.max(8, x),
        top: Math.max(8, y + 8),
        keyboard,
        selection,
      } : null);
    };

    const captureSelection = () => {
      if (document.activeElement?.closest('[data-assistant-ui]')) {
        return;
      }
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        if (document.activeElement?.closest('[data-assistant-ui]')) {
          return;
        }
        const selection = window.getSelection();
        const content = getSelectionReference(selection);
        if (!content || !selection) {
          setReference((current) => current?.selection ? null : current);
          return;
        }
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        showReference(content, rect.left, rect.bottom, false, true);
      });
    };

    const elementContent = (element: Element) => {
      const selection = window.getSelection();
      const selectedHere = selection?.rangeCount && selection.getRangeAt(0).intersectsNode(element);
      return (selectedHere ? getSelectionReference(selection) : '') || getElementReference(element);
    };

    const captureElement = (event: MouseEvent | KeyboardEvent) => {
      const keyboard = event instanceof KeyboardEvent || (event.clientX === 0 && event.clientY === 0);
      if ((!keyboard && event.shiftKey) || !(event.target instanceof Element) || !canReferenceElement(event.target)) {
        return;
      }
      const content = elementContent(event.target);
      if (!content) {
        return;
      }
      event.preventDefault();
      window.clearTimeout(holdTimer);
      window.cancelAnimationFrame(frameId);
      const rect = event.target.getBoundingClientRect();
      showReference(content, keyboard ? rect.left : event.clientX, keyboard ? rect.bottom : event.clientY, keyboard);
    };

    const dismiss = () => setReference(null);
    const cancelHold = () => {
      window.clearTimeout(holdTimer);
      touchOrigin = null;
    };
    const startHold = (event: PointerEvent) => {
      cancelHold();
      heldElement = null;
      if (event.pointerType !== 'touch' || !(event.target instanceof Element) || !canReferenceElement(event.target)) {
        return;
      }
      const element = event.target;
      touchOrigin = {x: event.clientX, y: event.clientY};
      holdTimer = window.setTimeout(() => {
        const content = elementContent(element);
        if (content) {
          heldElement = element;
          showReference(content, event.clientX, event.clientY);
        }
      }, 500);
    };
    const moveHold = (event: PointerEvent) => {
      if (touchOrigin && Math.hypot(event.clientX - touchOrigin.x, event.clientY - touchOrigin.y) > 10) {
        cancelHold();
      }
    };
    const preventHeldClick = (event: MouseEvent) => {
      if (event.target instanceof Node && heldElement?.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        heldElement = null;
      }
    };
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
        dismiss();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        captureElement(event);
      } else if (event.key === 'Escape' && menuRef.current) {
        event.preventDefault();
        event.stopPropagation();
        dismiss();
        returnFocusRef.current?.focus({preventScroll: true});
      }
    };
    document.addEventListener('selectionchange', captureSelection);
    document.addEventListener('contextmenu', captureElement);
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('pointerdown', startHold);
    document.addEventListener('pointermove', moveHold);
    document.addEventListener('pointerup', cancelHold);
    document.addEventListener('pointercancel', cancelHold);
    document.addEventListener('click', preventHeldClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('scroll', dismiss);
    window.addEventListener('resize', dismiss);
    return () => {
      window.cancelAnimationFrame(frameId);
      cancelHold();
      document.removeEventListener('selectionchange', captureSelection);
      document.removeEventListener('contextmenu', captureElement);
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('pointerdown', startHold);
      document.removeEventListener('pointermove', moveHold);
      document.removeEventListener('pointerup', cancelHold);
      document.removeEventListener('pointercancel', cancelHold);
      document.removeEventListener('click', preventHeldClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('scroll', dismiss);
      window.removeEventListener('resize', dismiss);
    };
  }, []);

  useEffect(() => {
    if (reference?.keyboard) {
      quoteRef.current?.focus({preventScroll: true});
    }
  }, [reference]);

  useEffect(() => {
    if (!reference || !menuRef.current) {
      return;
    }
    const rect = menuRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(reference.left, window.innerWidth - rect.width - 8));
    const top = Math.max(8, Math.min(reference.top, window.innerHeight - rect.height - 8));
    if (left !== reference.left || top !== reference.top) {
      setReference({...reference, left, top});
    }
  }, [error, reference]);

  if (!reference) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      data-assistant-ui
      className={styles.referenceMenu}
      style={{left: reference.left, top: reference.top}}
      role="dialog"
      aria-label={translate({message: '引用页面内容'})}
      onPointerDown={(event) => event.preventDefault()}>
      <button
        ref={quoteRef}
        type="button"
        onClick={() => {
          if (onReference(reference.content)) {
            setReference(null);
            window.getSelection()?.removeAllRanges();
          } else {
            setError(translate({message: '引用后将超出 {value0} 个字符，请缩小引用范围或精简已有问题。'}, {value0: MAX_QUESTION_LENGTH}));
          }
        }}>
        {translate({message: '引用到智能助理'})}
      </button>
      {error && <span role="alert">{error}</span>}
    </div>,
    document.body,
  );
}
